import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  createForwardPaymentIntent,
  listBillingMethods,
  saveBillingMethod,
  type BillingMethod,
  type ForwardPaymentIntent,
} from '@/services/payments.service';
import { getErrorMessage } from '@/services/apiClient';

type ForwardPaymentElement = {
  mount: (selectorOrElement: string | HTMLElement, options: Record<string, unknown>) => void;
  unmount?: () => void;
  destroy?: () => void;
};

type ForwardWindow = Window & {
  Forward?: {
    createPaymentElement: (config: { apiKey: string; clientSecret: string }) => Promise<ForwardPaymentElement>;
  };
};

interface ForwardBillingMethodCaptureProps {
  active: boolean;
  patientId: number;
  merchantId: number | null;
  onSaved: (method: BillingMethod) => void;
}

function normalizeCardType(cardType: string): string {
  if (cardType === 'american_express') return 'American Express';
  return cardType
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function buildCardTitle(cardType: string, lastFour: string, expMonth: string, expYear: string): string {
  if (!cardType || !lastFour || !expMonth || !expYear) return '';
  const normalizedCardType = normalizeCardType(cardType);
  const paddedMonth = expMonth.padStart(2, '0');
  const fullYear = expYear.length === 2 ? `20${expYear}` : expYear;
  return `${normalizedCardType} ****${lastFour} (${paddedMonth}/${fullYear})`;
}

function buildExpireDate(expMonth: string, expYear: string): string | undefined {
  if (!expMonth || !expYear) return undefined;
  const paddedMonth = expMonth.padStart(2, '0');
  const fullYear = expYear.length === 2 ? `20${expYear}` : expYear;
  return `${fullYear}-${paddedMonth}-01`;
}

export function ForwardBillingMethodCapture({
  active,
  patientId,
  merchantId,
  onSaved,
}: ForwardBillingMethodCaptureProps) {
  const queryClient = useQueryClient();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const paymentElementRef = useRef<ForwardPaymentElement | null>(null);
  const [intent, setIntent] = useState<ForwardPaymentIntent | null>(null);
  const [loadingIntent, setLoadingIntent] = useState(false);
  const [savingMethod, setSavingMethod] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const mountable = active && patientId > 0 && !!merchantId;

  useEffect(() => {
    if (!mountable) {
      setIntent(null);
      setError(null);
      setNotice(null);
      return;
    }

    let cancelled = false;
    setLoadingIntent(true);
    setError(null);
    setNotice(null);
    setIntent(null);

    createForwardPaymentIntent({
      amount: 1,
      description: `Payment method for patient ${patientId}`,
      merchant_id: merchantId ?? undefined,
      patient_id: patientId,
    })
      .then((result) => {
        if (!cancelled) {
          setIntent(result);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(getErrorMessage(err, 'Unable to prepare the Forward card form.'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingIntent(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [merchantId, mountable, patientId]);

  useEffect(() => {
    if (!mountable || !intent || !containerRef.current) return;
    if (!intent.public_api_key || !intent.client_secret) {
      setError('Forward did not return the required card-form credentials.');
      return;
    }

    const forwardWindow = window as ForwardWindow;
    if (!forwardWindow.Forward?.createPaymentElement) {
      setError('Forward.js is not available in this session.');
      return;
    }

    let cancelled = false;
    let expMonth = '';
    let expYear = '';
    let lastFour = '';
    let cardType = '';

    forwardWindow.Forward.createPaymentElement({
      apiKey: intent.public_api_key,
      clientSecret: intent.client_secret,
    })
      .then((paymentElement) => {
        if (cancelled) return;
        paymentElementRef.current = paymentElement;
        paymentElement.mount(containerRef.current as HTMLElement, {
          hidePostalCode: true,
          hideCountry: true,
          onChange: (event: { ready?: boolean; card?: { expMonth?: string; expYear?: string; brand?: string; lastFourDigits?: string } }) => {
            if (event.ready) {
              expMonth = event.card?.expMonth ?? '';
              expYear = event.card?.expYear ?? '';
              lastFour = event.card?.lastFourDigits ?? '';
              cardType = event.card?.brand ?? '';
            }
          },
          onReady: async (tokenize: () => Promise<void>) => {
            await tokenize();
          },
          onSuccess: async ({ token }: { token: string }) => {
            if (cancelled) return;

            setSavingMethod(true);
            setError(null);
            try {
              const expireDate = buildExpireDate(expMonth, expYear);
              await saveBillingMethod({
                patientId,
                paymentMethodToken: token,
                title: buildCardTitle(cardType, lastFour, expMonth, expYear),
                cardType,
                lastfour: lastFour,
                expireDate,
                processorEnumID: 3,
                accountAuthnetID: merchantId ?? undefined,
              });

              await queryClient.invalidateQueries({ queryKey: ['patient-billing-methods', patientId] });
              const methods = await listBillingMethods(patientId);
              const savedMethod = methods.find((method) => method.token === token);
              if (!savedMethod) {
                throw new Error('The new payment source was saved but could not be reloaded.');
              }

              setNotice(`Saved ${savedMethod.title}.`);
              onSaved(savedMethod);
            } catch (err) {
              setError(getErrorMessage(err, 'Failed to save the new payment source.'));
            } finally {
              setSavingMethod(false);
            }
          },
          onError: (err: unknown) => {
            if (!cancelled) {
              setError(getErrorMessage(err, 'Forward card entry failed.'));
            }
          },
        });
      })
      .catch((err) => {
        if (!cancelled) {
          setError(getErrorMessage(err, 'Unable to mount the Forward card form.'));
        }
      });

    return () => {
      cancelled = true;
      const paymentElement = paymentElementRef.current;
      if (paymentElement?.destroy) paymentElement.destroy();
      else if (paymentElement?.unmount) paymentElement.unmount();
      paymentElementRef.current = null;
      if (containerRef.current) containerRef.current.innerHTML = '';
    };
  }, [intent, merchantId, mountable, onSaved, patientId, queryClient]);

  const helperText = useMemo(() => {
    if (!merchantId) return 'Select a merchant account before entering a new card.';
    if (loadingIntent) return 'Preparing secure card entry...';
    if (savingMethod) return 'Saving the new payment source...';
    return '';
  }, [loadingIntent, merchantId, savingMethod]);

  if (!active) return null;

  return (
    <div className="space-y-2 rounded-md border border-blue-200 bg-blue-50 p-3">
      <div className="text-sm font-medium text-blue-900">New Payment Source</div>
      {helperText ? <p className="text-xs text-blue-800">{helperText}</p> : null}
      {notice ? <p className="text-xs text-green-700">{notice}</p> : null}
      {error ? <p className="text-xs text-red-700">{error}</p> : null}
      <div
        ref={containerRef}
        className="min-h-[120px] rounded-md border border-slate-300 bg-white p-4"
      />
    </div>
  );
}
